<template>
  <div
    ref='boxRef'
    class='box'
    :href='url'
    :data-id='isDevelopment ? id : null'
    :class='{ "is-hover": isHover }'
    @mouseover='onMouseOver'
    @mouseout='onMouseOut'
  >
    <a class='overlay' :href='url' @click='trackSelecteBox' />
    <div class='inner'>
      <div
        class='image'
        :class='{ [sizeWithXl]: true }'
      />
      <div class='details'>
        <div class='name'>
          {{ name }}
          <div class='size-tag'>
            <b>{{ squareText }}&nbsp;</b>
            <span>({{ capacityText }})</span>
          </div>
        </div>
        <div
          v-if='discounts.length'
          class='discounts'
        >
          <template
            v-for='(discount, index) in discounts'
            :key='index'
          >
            <span v-html='discount' />
            <span
              v-if='index != discounts.length - 1'
              class="separator"
            />
          </template>
        </div>
        <div class='dimensions'>
          <span>{{ dimensionsText }}</span>
          <span class='hint'>{{ tp('dimensions_hint') }}</span>
        </div>
        <div class='level'>
          {{ tp('level', { number: level, circle: isEntresol ? '2' : '1' }) }}
        </div>
      </div>
      <div class='price'>
        <span>
          {{ tp('from') }}
          <span
            v-if='priceWoDiscount != priceWithDiscount'
            class='price-wo-discount'
          >
            {{ priceWoDiscount }}
          </span>
          {{ priceWithDiscount }}{{ tp('month') }}
        </span>
        <div class='rent'>
          <a :href='url' @click='trackRentBox'>
            {{ tp('rent') }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StorageBox',
  inheritAttrs: false
};
</script>

<script setup>
import { ref, watch } from 'vue';

import track from '@/helpers/track';
import { isMobile } from '@/helpers/mobile_detect';
import { t, prepared } from '@/helpers/i18n';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, required: true },
  url: { type: String, required: true },
  level: { type: String, required: true },
  size: { type: String, required: true },
  capacity: { type: Number, required: true },
  isEntresol: { type: Boolean, required: true },
  discounts: { type: Array, required: true },
  squareText: { type: String, required: true },
  dimensionsText: { type: String, required: true },
  capacityText: { type: String, required: true },
  priceWoDiscount: { type: String, required: true },
  priceWithDiscount: { type: String, required: true },
  isHover: { type: Boolean, required: true }
});
const emit = defineEmits(['box:mouseover', 'box:mouseout']);
const tp = prepared('frontend.storage_schema_boxes.box');

const isDevelopment = window.ENV === 'development';
const boxRef = ref(null);

const XL_CAPACITY = 25.0;

const sizeWithXl = props.capacity > XL_CAPACITY ?
  'extra_large' :
  props.size;

watch(() => props.isHover, () => {
  if (!props.isHover) { return; }
  if (!boxRef.value) { return; }
  const containerNode = $(boxRef.value).closest('.size-groups')[0];
  if (!containerNode) { return; }

  const boxRect = boxRef.value.getBoundingClientRect();
  const containerRect = containerNode.getBoundingClientRect();

  // Check if the box is outside the visible area of the container
  if (boxRect.top < containerRect.top) {
    containerNode.scrollBy({
      top: boxRect.top - containerRect.top - 40 /* headline height */,
      behavior: 'smooth'
    });
  } else if (boxRect.bottom > containerRect.bottom) {
    boxRef.value.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });
  }
});

function onMouseOver() {
  if (isMobile()) { return; }

  emit('box:mouseover', { boxId: props.id });
}
function onMouseOut() {
  if (isMobile()) { return; }

  emit('box:mouseout', { boxId: props.id });
}
async function trackSelecteBox(e) {
  track({ event: 'select_box' });
}
async function trackRentBox(e) {
  // NOTE: Теперь ведет на просмотр бокса, поэтому изменяем событие.
  // track({ event: 'rent_box' });
  track({ event: 'select_box' });
}
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/responsive'
@import 'app/packs/stylesheets/mixins/typography'
@import 'app/packs/stylesheets/mixins/button_v4'

$image_path: '../../../images/vue/storage_schema_boxes/box'

.box
  color: var(--gray-900)
  position: relative
  // background: #fff

  +gte_laptop
    border-bottom: 1px solid var(--gray-200)
    margin-bottom: 12px

    &:hover
      border-bottom-color: transparent
      color: var(--gray-900)
      text-decoration: none

  // &:active .name, // - disable active color in mobile version
  &.is-hover
    +gte_laptop
      border-radius: 12px
      // background: #fff
      box-shadow: 0px 8px 20px 0px rgba(3, 3, 3, 0.10)

    .name
      color: var(--primary-color)

  &:active
    color: var(--gray-900)
    text-decoration: none

    .name
      color: var(--primary-color-active)

.overlay
  bottom:0
  left:0
  position: absolute
  right:0
  top:0

.inner
  display: flex
  pointer-events: none
  position: relative
  z-index: 1

  +lte_ipad
    border-radius: rem4(24px)
    box-shadow: -4px 8px 20px 0px rgba(0, 0, 0, 0.10)
    display: flex
    flex-direction: column
    gap: rem4(16px)
    overflow: hidden
    padding-bottom: rem4(24px)
    width: 100% !important // !important is needed to fix swiper changing box width to 100%

  +gte_laptop
    align-items: center
    gap: 32px
    padding: 12px

.image
  +lte_ipad
    align-items: center
    background: var(--gray-100)
    display: flex
    height: rem4(226px)
    justify-content: center
    width: 100%

    &:before
      background-position: center
      background-repeat: no-repeat
      background-size: cover
      content: ''

  +gte_laptop
    background-position: center
    background-repeat: no-repeat
    background-size: cover
    height: 120px
    width: 120px

  @each $size in ultra_small, small, medium, large, extra_large
    &.#{$size}:before
      +lte_ipad
        background-image: url('#{$image_path}/size/mobile/#{$size}.png')

    &.#{$size}
      +gte_laptop
        background-image: url('#{$image_path}/size/desktop/#{$size}.png')

    +lte_ipad
      &.ultra_small:before
        height: rem4(184px)
        width: rem4(175px)

      &.small:before
        height: rem4(200px)
        width: rem4(121px)

      &.medium:before
        height: rem4(180px)
        width: rem4(200px)

      &.large:before
        height: rem4(155px)
        width: rem4(200px)

      &.extra_large:before
        height: rem4(226px)
        width: rem4(226px)

.details
  +p3
  display: flex
  flex-direction: column

  +lte_ipad
    gap: rem4(8px)
    padding: 0 rem4(20px)

  +gte_laptop
    gap: 8px
    padding: 0 20px

.name
  +p3(700)
  display: flex
  align-items: center
  transition: color var(--transition-duration)

  +lte_ipad
    gap: rem4(8px)

  +gte_laptop
    gap: 8px

  .size-tag
    background: rgba(255, 79, 0, 0.10)
    border-radius: 100px
    white-space: nowrap

    +lte_ipad
      padding: rem4(4px) rem4(12px)
      gap: rem4(10px)

    +gte_laptop
      padding: 4px 12px
      gap: 10px

    b
      +p3(700)
      color: var(--gray-900)

    span
      +p3(500)
      color: var(--gray-500)

.discounts
  color: #00b929
  flex-wrap: wrap

  .separator
    background: #00b929

.discounts,
.dimensions
  display: flex

  +lte_ipad
    gap: rem4(8px)

  +gte_laptop
    gap: 8px

  .hint
    color: var(--gray-400)

.separator
  align-self: center
  background: var(--gray-900)

  +lte_ipad
    width: rem4(2px)
    height: rem4(2px)

  +gte_laptop
    width: 2px
    height: 2px

.price
  display: flex
  flex-direction: column
  white-space: nowrap

  +lte_ipad
    padding: 0 rem4(20px)
    gap: rem4(16px)
    margin-top: rem(8px)

  +gte_laptop
    align-items: flex-end
    gap: 16px
    margin-left: auto

  span
    +p3(700)

  .price-wo-discount
    +p3(500)
    color: var(--gray-500)
    padding: 0 rem4(2px)
    text-decoration: line-through

    +gte_laptop
      letter-spacing: 0.16px

  .rent a
    +button_v4('primary', 'sm')
    pointer-events: all

    +lte_ipad
      width: 100%
</style>
